import { Component, ChangeDetectionStrategy, HostListener, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { BrokerProfileService } from './broker-profile.service';
import { BrokerProfileMode } from './broker-profile.models';
import { BrokerProfileCaseBodyComponent } from './broker-profile-case-body.component';
import { BrokerProfileClientFacingBodyComponent } from './broker-profile-clientfacing-body.component';
import { StageStateService } from '../../services/stage-state.service';
import { CaseContextService } from '../../../../core/services/case-context.service';

/**
 * Broker-tier product profile flyout (single-slot). Mounted once at the
 * Calculator V2 shell. Opens from Stage 04 Compare column headers (Phase 1)
 * and later from Match cards, rate-sheet rows, and grid rows.
 *
 * Mockup: docs/competitive/mockups/broker_profile_v1/01-fit-tab.html
 * Plan:   docs/plans/2026-04-23-broker-profile-flyout.md
 *
 * Commit 1 ships the shell: backdrop + header chrome + empty body placeholder.
 * Fit zone, Product zone, and Accordion arrive in Commit 2.
 */
@Component({
  selector: 'app-broker-profile-flyout',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, BrokerProfileCaseBodyComponent, BrokerProfileClientFacingBodyComponent],
  template: `
    @if (ctx(); as c) {
      <div class="bp-backdrop" (click)="close()"></div>
      <aside class="bp-flyout"
             [class.bp-flyout--narrow]="c.mode === 'client-facing'"
             role="dialog" aria-label="Product profile"
             (click)="$event.stopPropagation()">
        <header class="bp-hd">
          <div class="bp-hd-logo" aria-hidden="true">
            <span class="bp-hd-logo-text">{{ shortCarrier(c.product.companyName) }}</span>
          </div>

          <div class="bp-hd-id">
            <div class="bp-hd-id-r1">
              <span class="bp-source-pill">{{ sourceLabel() }} · #{{ c.rank }}</span>
              <span class="bp-prod-name">{{ c.product.productName }}</span>
              @if (c.product.bestid) {
                <span class="bp-ambest">{{ c.product.bestid }}</span>
              }
              @if (caseFit(); as fit) {
                <span class="fit-badge" [class.fit-yes]="fit.fits" [class.fit-no]="!fit.fits">
                  @if (fit.fits) {
                    ✓ Fits {{ caseCtx.activeCase()?.name }}
                  } @else {
                    ⚠ {{ fit.reason }}
                  }
                </span>
              }
            </div>
            <div class="bp-hd-id-r2">
              <span class="bp-company">{{ c.product.companyName }}</span>
            </div>
          </div>

          <div class="bp-hd-actions">
            <div class="bp-mode-toggle" role="tablist" aria-label="Presentation mode">
              <button [class.active]="c.mode === 'case'"
                      (click)="setMode('case')">Case</button>
              <button [class.active]="c.mode === 'client-facing'"
                      (click)="setMode('client-facing')">Client-facing</button>
            </div>
            <button class="bp-close" aria-label="Close" (click)="close()">✕</button>
          </div>
        </header>

        <div class="bp-body"
             [class.bp-body--clientfacing]="c.mode === 'client-facing'">
          @if (c.mode === 'case') {
            <app-broker-profile-case-body
              [product]="c.product"
              [productType]="c.productType"
              [peers]="c.peers"
              [scenario]="c.scenario"
              [rank]="c.rank"
              (illustrate)="onIllustrate()"
              (backToMatch)="close()" />
          } @else if (c.mode === 'client-facing') {
            <app-broker-profile-clientfacing-body
              [product]="c.product"
              [productType]="c.productType"
              [rateDisplay]="effectiveRateDisplay()"
              (runInCalculator)="onRunInCalculator()" />
          }
        </div>

        @if (c.mode !== 'client-facing') {
          <footer class="bp-foot">
            <span class="bp-foot-meta">Broker profile · <b>{{ c.mode }}</b> mode</span>
            @if (c.mode === 'case') {
              <button class="bp-btn bp-btn-primary" (click)="onIllustrate()">
                Illustrate this product →
              </button>
            }
          </footer>
        }
      </aside>
    }
  `,
  styles: [`
    :host { display: contents; }

    .bp-backdrop {
      position: fixed; inset: 0;
      background: rgba(13, 42, 69, 0.35);
      z-index: 950;
    }
    .bp-flyout {
      position: fixed; top: 0; right: 0; bottom: 0;
      width: 950px; max-width: 92vw;
      background: var(--panel);
      box-shadow: -4px 0 30px rgba(13, 42, 69, 0.25);
      display: flex; flex-direction: column;
      z-index: 951;
      font-family: var(--sans);
      color: var(--text);
      transition: width 0.2s ease;
    }
    .bp-flyout--narrow {
      width: 480px;
    }

    .bp-hd {
      flex-shrink: 0;
      padding: 10px 16px;
      border-bottom: 1px solid var(--border-light);
      background: var(--panel);
      display: flex; align-items: center; gap: 14px;
    }
    .bp-hd-logo {
      width: 92px; height: 48px;
      background: var(--panel2);
      border: 1px solid var(--border-light);
      border-radius: 3px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .bp-hd-logo-text {
      font-family: var(--sans);
      font-weight: 800;
      letter-spacing: -0.01em;
      line-height: 1;
      font-size: 14px;
      color: var(--blue-2);
    }

    .bp-hd-id { flex: 1; min-width: 0; }
    .bp-hd-id-r1 { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .bp-hd-id-r2 { margin-top: 3px; }

    .bp-source-pill {
      font-family: var(--mono);
      font-size: 9px; font-weight: 700;
      background: var(--navy); color: #fff;
      padding: 3px 8px; border-radius: 2px;
      letter-spacing: 0.1em; text-transform: uppercase;
    }
    .bp-prod-name {
      font-size: 15px; font-weight: 700;
      color: var(--navy);
    }
    .bp-ambest {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      background: var(--blue-10); color: var(--blue);
      padding: 2px 7px; border-radius: 3px;
    }
    .bp-company {
      font-size: 11px; font-weight: 600;
      color: var(--muted);
    }

    .bp-hd-actions {
      display: flex; gap: 8px; align-items: center;
      flex-shrink: 0;
    }

    .bp-mode-toggle {
      display: flex;
      border: 1px solid var(--border);
      border-radius: 3px;
      overflow: hidden;
    }
    .bp-mode-toggle button {
      font-family: var(--sans);
      font-size: 11px; font-weight: 600;
      padding: 5px 11px;
      border: none;
      background: var(--panel);
      color: var(--muted);
      cursor: pointer;
      border-right: 1px solid var(--border);
      letter-spacing: 0.02em;
    }
    .bp-mode-toggle button:last-child { border-right: none; }
    .bp-mode-toggle button.active {
      background: var(--navy);
      color: #fff;
    }

    .bp-close {
      width: 28px; height: 28px;
      border: 1px solid var(--border-light);
      border-radius: 3px;
      background: var(--panel);
      color: var(--muted);
      font-size: 14px;
      cursor: pointer;
    }
    .bp-close:hover {
      background: var(--panel2);
      color: var(--navy);
    }

    .bp-body {
      flex: 1;
      overflow-y: auto;
      background: var(--bg);
      padding: 18px 20px 28px;
    }

    .bp-placeholder {
      background: var(--panel);
      border: 1px dashed var(--border);
      border-radius: 4px;
      padding: 36px 28px;
      text-align: center;
    }
    .bp-placeholder-eyebrow {
      font-family: var(--mono);
      font-size: 10px; font-weight: 700;
      color: var(--muted);
      letter-spacing: 0.14em; text-transform: uppercase;
    }
    .bp-placeholder-title {
      font-size: 16px; font-weight: 700;
      color: var(--navy);
      margin-top: 6px;
    }
    .bp-placeholder-sub {
      font-size: 12px;
      color: var(--muted);
      margin-top: 4px;
    }

    .bp-foot {
      flex-shrink: 0;
      padding: 14px 20px;
      border-top: 1px solid var(--border);
      background: var(--panel);
      display: flex; align-items: center; gap: 12px;
    }
    .bp-foot-meta {
      font-family: var(--mono);
      font-size: 10px;
      color: var(--muted);
      letter-spacing: 0.04em;
      margin-right: auto;
    }
    .bp-foot-meta b {
      color: var(--text);
      font-weight: 700;
      text-transform: capitalize;
    }

    .bp-btn {
      font-family: var(--sans);
      font-size: 13px; font-weight: 600;
      padding: 10px 16px;
      border: 1px solid var(--border);
      border-radius: 3px;
      background: var(--panel);
      color: var(--text);
      cursor: pointer;
    }
    .bp-btn-primary {
      background: var(--navy);
      border-color: var(--navy);
      color: #fff;
    }
    .bp-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .fit-badge {
      display: inline-flex; align-items: center; gap: 4px;
      padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 600;
      letter-spacing: 0.3px; font-family: 'Source Sans 3', sans-serif;
      white-space: nowrap;
    }
    .fit-badge.fit-yes {
      background: rgba(110, 231, 183, 0.18);
      color: #10B981;
      border: 1px solid rgba(110, 231, 183, 0.5);
    }
    .fit-badge.fit-no {
      background: rgba(252, 165, 165, 0.18);
      color: #C53030;
      border: 1px solid rgba(252, 165, 165, 0.5);
    }
  `],
})
export class BrokerProfileFlyoutComponent {
  private readonly svc = inject(BrokerProfileService);
  private readonly state = inject(StageStateService);
  private readonly router = inject(Router);
  readonly caseCtx = inject(CaseContextService);

  readonly ctx = this.svc.context;

  /**
   * B2 — Case-fit badge. Checks hard filters from the active case against
   * the product currently open in the flyout. Returns null when no case is
   * active (badge hidden). Returns { fits: true } or { fits: false, reason }
   * when a case is active.
   *
   * MatchSelection is a GMWB/IA-centric model that doesn't carry states[],
   * maxIssueAge, minContribution, or bands[] — those checks degrade
   * gracefully (skip) for the current VA/IA product type. Band logic will
   * become meaningful once FA products are wired in.
   */
  readonly caseFit = computed(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return null;
    const p = this.ctx()?.product as any;
    if (!p) return null;

    // State check
    if (c.state && Array.isArray(p.states) && p.states.length > 0 && !p.states.includes(c.state)) {
      return { fits: false, reason: `Not licensed in ${c.state}` };
    }
    // Max issue age check
    if (c.age != null && p.maxIssueAge != null && p.maxIssueAge < c.age) {
      return { fits: false, reason: `Max issue age ${p.maxIssueAge} below age ${c.age}` };
    }
    // Min premium / band check
    if (c.premium != null) {
      if (Array.isArray(p.bands) && p.bands.length > 0) {
        const lowestMin = Math.min(...p.bands.map((b: any) => b.min));
        if (lowestMin > c.premium) {
          return { fits: false, reason: `Minimum $${(lowestMin / 1000).toFixed(0)}K above premium` };
        }
      } else if (p.minContribution != null && p.minContribution > c.premium) {
        return { fits: false, reason: `Minimum $${(p.minContribution / 1000).toFixed(0)}K above premium` };
      }
    }
    return { fits: true, reason: '' };
  });

  /**
   * B2 — Case-aware rate kicker. For FA products with band data, picks the
   * best-fitting rate band for the case premium and returns a labelled rate.
   * Returns null for IA/VA/RILA (no bands) — template falls back to the
   * generic rateDisplay passed from the rate-sheet opener.
   */
  readonly caseAwareRate = computed(() => {
    const c = this.caseCtx.activeCase();
    const p = this.ctx()?.product as any;
    if (!c || !p || c.premium == null) return null;
    if (Array.isArray(p.bands) && p.bands.length > 0) {
      let best = p.bands[0];
      for (const b of p.bands) {
        if (c.premium >= b.min) best = b;
      }
      return {
        value: best.rate as number,           // decimal e.g. 0.0595
        bandMin: best.min as number,
        label: `${c.name}'s rate ($${(best.min / 1000).toFixed(0)}K band)`,
      };
    }
    return null;
  });

  /**
   * Returns an overridden RateDisplay when the case-aware rate is available
   * (FA product with band data + active case with premium), otherwise falls
   * back to whatever the context carries from the rate-sheet opener.
   */
  readonly effectiveRateDisplay = computed(() => {
    const r = this.caseAwareRate();
    const ctx = this.ctx();
    if (r) {
      return {
        kicker: r.label,
        value: r.value * 100,   // RateDisplay.value is whole percent
        meta: undefined,
      };
    }
    return ctx?.rateDisplay;
  });

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.svc.isOpen()) this.close();
  }

  close(): void {
    this.svc.close();
  }

  setMode(mode: BrokerProfileMode): void {
    this.svc.setMode(mode);
  }

  /**
   * Illustrate CTA. Pins the profile's product as the case winner, closes
   * the flyout, and routes to Stage 05 with caseId preserved so the
   * illustration loads against the current case.
   */
  onIllustrate(): void {
    const ctx = this.ctx();
    if (!ctx) return;
    this.state.caseState.update(cs => ({ ...cs, winnerGmwbId: ctx.product.gmwbId }));
    const caseId = this.state.caseState().caseId;
    this.svc.close();
    this.router.navigate(['/calculator-v2/illustrate'], {
      queryParams: caseId ? { caseId } : undefined,
    });
  }

  /**
   * Run-in-Calculator CTA (Client-facing mode). Deep-links to Stage 01
   * with intent URL seed params — StageClient mints a new case per
   * docs/calculator_v2_intent_url.md and the D2 decision.
   */
  onRunInCalculator(): void {
    const ctx = this.ctx();
    if (!ctx) return;
    const seedState = ctx.scenario?.stateCode ?? 'IL';
    const seedContribution = ctx.scenario?.contribution ?? 100000;
    const seedDeferral = ctx.scenario?.deferralYear ?? 10;
    this.svc.close();
    this.router.navigate(['/calculator-v2/client'], {
      queryParams: {
        seedProductId: ctx.product.productid,
        seedProductType: ctx.productType,
        seedContribution,
        seedDeferral,
        seedState,
      },
    });
  }

  readonly sourceLabel = computed(() => {
    switch (this.ctx()?.source) {
      case 'stage04-compare': return 'Case';
      case 'match-card': return 'Match';
      case 'rate-sheet': return 'Rate sheet';
      case 'grid': return 'Grid';
      default: return 'Case';
    }
  });

  shortCarrier(full: string): string {
    return full.split(' ')[0]?.toUpperCase() ?? 'CARRIER';
  }
}
